import Button from '@/common/components/base/button/Button';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import useDebounce from '@/common/hooks/useDebounce';
import { getVehicleColorOption } from '@/common/queries/selections/vehicleColor/vehicleColorOption';
import { getVehicleProductYearOption } from '@/common/queries/selections/vehicleProductYear/vehicleProductYearOption';
import { InsuranceABV } from '@/common/types/SelectionOptions.type';
import { requiredText } from '@/common/utils/rules/publicRules';
import { getCulpritVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritVehicleInformation/getCulpritVehicleInformationOption';
import { patchCulpritPartTwoVehicleInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchCulpritPartTwoVehicleInformation/patchCulpritPartTwoVehicleInformationOption';
import { VehicleCulpritInformationPartTwoFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Flex } from 'styled-system/jsx';

const PartTwoCulprit = () => {
  const methods = useForm<VehicleCulpritInformationPartTwoFormType>({
    mode: 'onChange',
  });
  const { id } = useParams();
  const [colorName, setColorName] = useState('');
  const { data: fileDetail } = useQuery(fileDetailQueryOption({ id: +id! }));
  const navigate = useNavigate();
  const debounce = useDebounce();
  const { data: vehicleColors } = useQuery({
    ...getVehicleColorOption({
      insurance: fileDetail?.insurance as InsuranceABV,
      name: colorName,
    }),
    enabled: !!fileDetail?.insurance,
  });
  const { data: vehicleProductsYears } = useQuery(
    getVehicleProductYearOption(),
  );
  const { mutate: culpritPartTwoMutation } = useMutation(
    patchCulpritPartTwoVehicleInformationOption(),
  );

  const submitHandler = (data: VehicleCulpritInformationPartTwoFormType) => {
    culpritPartTwoMutation(
      {
        id: +id!,
        data: {
          vehicle_manufacturing_year: data.vehicle_manufacturing_year?.label!,
          vehicle_chassis: data.vehicle_chassis!,
          vehicle_engine: data.vehicle_engine!,
          vehicle_color: data.vehicle_color?.value!,
          vehicle_vin: data.vehicle_vin!,
        },
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };
  const { data: vehicleInformationData } = useQuery(
    getCulpritVehicleInformationOption({
      id: +id!,
    }),
  );
  useEffect(() => {
    if (vehicleInformationData) {
      methods.reset({
        vehicle_manufacturing_year:
          vehicleInformationData?.vehicle_manufacturing_year
            ? {
                label: vehicleInformationData?.vehicle_manufacturing_year,
                value: vehicleInformationData?.vehicle_manufacturing_year,
              }
            : null,
        vehicle_color: vehicleInformationData?.vehicle_color
          ? {
              label: vehicleInformationData?.vehicle_color.name,
              value: vehicleInformationData?.vehicle_color.id,
            }
          : null,
        vehicle_vin: vehicleInformationData?.vehicle_vin,
        vehicle_chassis: vehicleInformationData?.vehicle_chassis,
        vehicle_engine: vehicleInformationData?.vehicle_engine,
      });
    }
  }, [vehicleInformationData]);

  const handleColorOnChange = (value: string) => {
    if (value) {
      debounce(() => {
        setColorName(value);
      }, 1000);
    } else {
      setColorName(value);
    }
  };
  return (
    <FormProvider {...methods}>
      <Flex direction="column" gap={1}>
        <ModalInput
          name="vehicle_manufacturing_year"
          label="سال ساخت"
          placeholder="انتخاب کنید"
          options={vehicleProductsYears?.map((item) => ({
            label: item.fa_const,
            value: item.const,
          }))}
          rules={{ required: { message: requiredText, value: true } }}
        />

        <ModalInput
          name="vehicle_color"
          label="رنگ وسیله‌نقلیه"
          placeholder="انتخاب کنید"
          options={vehicleColors?.map((item) => {
            return {
              label: item.name,
              value: item.id,
            };
          })}
          searchOptions={{
            onChange: handleColorOnChange,
            placeholder: 'جستجو رنگ',
          }}
          rules={{ required: { message: requiredText, value: true } }}
        />
        <TextInput
          name="vehicle_vin"
          label="شماره VIN"
          placeholder="شماره VIN را وارد کنید"
          max={17}
          rules={{
            required: { message: requiredText, value: true },
            pattern: {
              value: /^[A-Z0-9]{17}$/,
              message:
                'شماره VIN باید 17 کاراکتر و شامل حروف بزرگ انگلیسی و یا اعداد باشد',
            },
          }}
        />
        <TextInput
          name="vehicle_chassis"
          label="شماره شاسی"
          placeholder="شماره شاسی را وارد کنید"
          rules={{ required: { message: requiredText, value: true } }}
        />
        <TextInput
          name="vehicle_engine"
          label="شماره موتور"
          placeholder="شماره موتور را وارد کنید"
          rules={{ required: { message: requiredText, value: true } }}
        />
        <Button
          onClick={methods.handleSubmit(submitHandler)}
          type="submit"
          width="100%"
          marginTop="6px"
          marginBottom="24px"
          disabled={!methods?.formState?.isValid}
        >
          ثبت
        </Button>
      </Flex>
    </FormProvider>
  );
};

export default PartTwoCulprit;
